// Find and cut off a FAKE EDGE BAND — a strip along a side of an image that
// is not picture but a copy of the picture just inside it.
//
// ─── WHY ────────────────────────────────────────────────────────────────────
//
// #1247 « Honeymoon 2026 » (2026-10-18): the client sent a PDF of her photos;
// the importer turned each page into a photo, and every one of the 160 showed a
// doubled strip — her trousers twice at the bottom, a flower band twice at the
// top. Measured on the 952×1400 display tier: a sharp seam at 5.9–6.0 % from
// the top and 93.9 % (bottom band ≈ 6.1 %), and vertical seams at 1.4 % and
// 98.4 % of the width.
//
// Two producers make such a strip, and this file does not care which:
//   · a print BLEED a design tool filled by repeating, mirroring or smearing
//     the outermost pixels of the photo;
//   · our own importer up to 2026-10-18 (pdfRaster.composeInteriorPageImage):
//     it fitted a 3:4 page inside the 200×297 trim and filled the letterbox
//     with a cover-fit, i.e. ZOOMED, copy of the same page. That is exactly
//     what #1247 shows — 3:4 inside 200 mm is 266.7 mm, (297 − 266.7)/2 + 3 mm
//     bleed = 18.2 mm of 303 mm = 6.0 %, and the vertical seams sit on the 3 mm
//     bleed line (3/206 = 1.46 %).
//
// ─── HOW: COMPARE, DON'T ASSUME ─────────────────────────────────────────────
//
// For each side, within the outer 10 %, a band is cut only when the picture
// INSIDE explains it:
//   · duplicate / mirror / smear of the edge rows (a design tool's bleed):
//     every depth is tried; the copy must match to within noise AND stop
//     matching when shifted a few rows — natural smoothness matches a little
//     at any offset, a real copy only at its own;
//   · a zoomed copy of the whole picture about its centre (our old importer,
//     opt-in): tested as a STRUCTURE — the per-row copy error drops to noise
//     across the band and jumps at the seam, at the same depth on opposite
//     sides.
// A horizon is a jump with nothing copied across it; a plain photo has no
// copy anywhere. No evidence → nothing is cut.
//
// Pure: works on a single-channel luma array, so the browser importer (canvas)
// and the repair script (sharp) run the very same code. Returns FRACTIONS of
// each side, so detection can run on a downscaled copy and the crop on the
// full-resolution file.

export interface LumaImage {
  width: number;
  height: number;
  /** One luma value (0–255) per pixel, row-major. */
  data: ArrayLike<number>;
}

export type Side = 'top' | 'right' | 'bottom' | 'left';
export type BandModel = 'duplicate' | 'mirror' | 'smear' | 'zoom';

export interface BandFinding {
  side: Side;
  /** Pixels cut from this side (at the detection resolution), seam row included. */
  depth: number;
  /** depth as a fraction of the image dimension across this side. */
  frac: number;
  model: BandModel;
  /** zoom factor for `zoom`, 1 otherwise. */
  scale: number;
  /** how sharply the copy stops fitting past the band (shifted or past-seam
   *  error ÷ band error); 0 for a band taken from its opposite side. */
  seamRatio: number;
  /** mean |band − copy| (0–255). */
  error: number;
  /** mean |band − band mean| (0–255): what `error` is judged against. */
  texture: number;
  /** error of the same copy shifted off its offset — specificity check. */
  shiftedError: number;
}

export interface EdgeBands {
  /** Fractions of the height (top/bottom) or width (left/right) to cut. */
  top: number;
  right: number;
  bottom: number;
  left: number;
  findings: BandFinding[];
}

/** Never look deeper than this — a fake band wider than 10 % is not a bleed. */
export const EDGE_BAND_MAX_FRAC = 0.10;

/** Thresholds — strong evidence only. Tuned on #1247's 160 pages, #cacdbfcb's
 *  40, their trimmed interiors (which must come back clean) and 54 ordinary
 *  customer photos (which must never be cut).
 *
 *  A copied band matches to within JPEG noise; natural smoothness over a few
 *  rows does not get under these (a 4-row "smear" of a plain photo scored
 *  0.23 of texture and 0.47 of its shifted twin). */
const COPY_MAX_ERR_VS_TEXTURE = 0.15;
const COPY_MAX_ERR_VS_SHIFTED = 0.4;
const SMEAR_MAX_ERR = 1.25;
const BAND_MIN_TEXTURE = 2.5;

export function rgbaToLuma(rgba: ArrayLike<number>, width: number, height: number, channels = 4): LumaImage {
  const out = new Float32Array(width * height);
  for (let i = 0, p = 0; i < out.length; i++, p += channels) {
    out[i] = 0.299 * rgba[p] + 0.587 * rgba[p + 1] + 0.114 * rgba[p + 2];
  }
  return { width, height, data: out };
}

/** A side seen as rows: u runs along the side (0..W), v runs inward from the
 *  edge (0 = outermost row, H-1 = the opposite edge). */
interface View { W: number; H: number; at: (u: number, v: number) => number }

function makeView(img: LumaImage, side: Side): View {
  const { width: w, height: h, data: d } = img;
  switch (side) {
    case 'top': return { W: w, H: h, at: (u, v) => d[v * w + u] };
    case 'bottom': return { W: w, H: h, at: (u, v) => d[(h - 1 - v) * w + u] };
    case 'left': return { W: h, H: w, at: (u, v) => d[u * w + v] };
    case 'right': return { W: h, H: w, at: (u, v) => d[u * w + (w - 1 - v)] };
  }
}

function sample(view: View, u: number, v: number): number {
  const uu = Math.min(Math.max(u, 0), view.W - 1);
  const vv = Math.min(Math.max(v, 0), view.H - 1);
  const u0 = Math.floor(uu), v0 = Math.floor(vv);
  const u1 = Math.min(u0 + 1, view.W - 1), v1 = Math.min(v0 + 1, view.H - 1);
  const fu = uu - u0, fv = vv - v0;
  const a = view.at(u0, v0), b = view.at(u1, v0), c = view.at(u0, v1), e = view.at(u1, v1);
  return (a * (1 - fu) + b * fu) * (1 - fv) + (c * (1 - fu) + e * fu) * fv;
}

function median(xs: number[]): number {
  if (xs.length === 0) return 0;
  const s = [...xs].sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

type Mapper = (u: number, v: number) => [number, number];

/** Mean |band(u,v) − picture(map(u,v))| over the band rows [0, k−1) (the row
 *  touching the seam is skipped: resampling blends it). */
function copyError(view: View, k: number, map: Mapper, uStep: number, vStep: number): number {
  const vEnd = k >= 3 ? k - 1 : k;
  let s = 0, n = 0;
  for (let v = 0; v < vEnd; v += vStep) {
    for (let u = 0; u < view.W; u += uStep) {
      const [mu, mv] = map(u, v);
      s += Math.abs(view.at(u, v) - sample(view, mu, mv));
      n++;
    }
  }
  return n ? s / n : Infinity;
}

function bandTexture(view: View, k: number, uStep: number, vStep: number): number {
  const vEnd = k >= 3 ? k - 1 : k;
  let s = 0, n = 0;
  for (let v = 0; v < vEnd; v += vStep) for (let u = 0; u < view.W; u += uStep) { s += view.at(u, v); n++; }
  const mean = s / n;
  let t = 0;
  for (let v = 0; v < vEnd; v += vStep) for (let u = 0; u < view.W; u += uStep) t += Math.abs(view.at(u, v) - mean);
  return t / n;
}

// ─── A BLEED MADE OF THE PHOTO'S OWN EDGE ROWS ───────────────────────────────
//
// Three ways a design tool fills a bleed, three maps from a band row v (0 =
// outermost) to the picture inside the seam at depth k:
//   duplicate  v ↦ v + k          (the next k rows, copied out)
//   mirror     v ↦ 2k − 1 − v     (folded at the seam — NO jump at the seam)
//   smear      v ↦ k              (the edge row stretched — no jump either)
// Mirror and smear are continuous across the seam, so the seam is not found
// by a jump: every depth k is TRIED, and a depth is accepted only when its
// copy error is small against the band's texture AND clearly smaller than the
// same copy shifted off by a few rows. Natural smoothness fails the second
// test: shifting a gradient by 2 rows barely changes its error, while a real
// copy only lines up at its own offset.

interface Fit { model: BandModel; error: number; shiftedError: number; texture: number; k: number }

const EDGE_MODELS: Array<{ model: 'duplicate' | 'mirror' | 'smear'; map: (k: number, dv: number) => Mapper }> = [
  { model: 'duplicate', map: (k, dv) => (u, v) => [u, v + k + dv] },
  { model: 'mirror', map: (k, dv) => (u, v) => [u, 2 * k - 1 - v + dv] },
  { model: 'smear', map: (k, dv) => (u) => [u, k + dv] },
];

/** Share of the seam row that is not the row's dominant flat value. */
function busyFraction(view: View, k: number, uStep: number): number {
  const vals: number[] = [];
  for (let u = 0; u < view.W; u += uStep) vals.push(view.at(u, k));
  const m = median(vals);
  return vals.filter((x) => Math.abs(x - m) > 8).length / vals.length;
}

function detectSide(img: LumaImage, side: Side): BandFinding | null {
  const view = makeView(img, side);
  const maxDepth = Math.floor(view.H * EDGE_BAND_MAX_FRAC);
  // A bleed is millimetres, not a pixel row: 0.6 % ≈ 1.8 mm on 303 mm.
  const minDepth = Math.max(3, Math.ceil(view.H * 0.006));
  if (maxDepth <= minDepth || view.W < 16) return null;
  const uStep = Math.max(1, Math.floor(view.W / 240));
  let best: Fit | null = null;
  for (const { model, map } of EDGE_MODELS) {
    let modelBest: Fit | null = null;
    for (let k = minDepth; k <= maxDepth; k++) {
      const shift = Math.max(2, Math.round(k / 4));
      if (model !== 'smear' && 2 * k + shift >= view.H) break;
      const vStep = Math.max(1, Math.floor(k / 20));
      const texture = bandTexture(view, k, uStep, vStep);
      if (texture < BAND_MIN_TEXTURE) continue;
      const error = copyError(view, k, map(k, 0), uStep, vStep);
      if (error > COPY_MAX_ERR_VS_TEXTURE * texture) continue;
      const shiftedError = copyError(view, k, map(k, shift), uStep, vStep);
      // …and the shifted copy must really FAIL: inside a smeared band every
      // map fits everything (0 ≤ 0.4 × 0), which proves nothing.
      if (error > COPY_MAX_ERR_VS_SHIFTED * shiftedError || shiftedError < Math.max(4, 0.15 * texture)) continue;
      // A smear is the weakest claim — sky, a wall or a row of poles is
      // nearly constant down a few rows too (4 of 54 real photos scored as a
      // ~1 % smear at the top). A tool's smear repeats ONE row exactly.
      if (model === 'smear' && error > SMEAR_MAX_ERR) continue;
      // A photo's edge is busy all along; a UI margin (white panel, thin
      // rules) is flat almost everywhere and repeats trivially.
      if (busyFraction(view, k, uStep) < 0.5) continue;
      const fit: Fit = { model, error, shiftedError, texture, k };
      // A smear stays a smear at any shallower depth, so its band ends at the
      // DEEPEST depth that still fits; a duplicate or mirror lines up only at
      // its own depth, so the best fit is the one.
      if (!modelBest
        || (model === 'smear' ? k > modelBest.k : error / texture < modelBest.error / modelBest.texture)) modelBest = fit;
    }
    if (modelBest && (!best || modelBest.error / modelBest.texture < best.error / best.texture)) best = modelBest;
  }
  if (!best) return null;
  // Cut the seam row too: resampling blends it with the band.
  const depth = Math.min(best.k + 1, maxDepth);
  return {
    side, depth, frac: depth / view.H, model: best.model, scale: 1,
    seamRatio: best.shiftedError / Math.max(best.error, 0.75),
    error: best.error, texture: best.texture, shiftedError: best.shiftedError,
  };
}

// ─── THE ZOOMED BACKGROUND ──────────────────────────────────────────────────
//
// A zoomed background is ONE picture behind the whole page, centred: every
// band of it obeys the same map p ↦ c + (p − c)/s. So it is tested as a
// STRUCTURE, not side by side: per-row copy error e(v) = mean |row v − the
// zoomed picture at row v| is low across a band and high on the real photo
// (a photo is not a zoom of itself), and the jump sits at the same depth on
// opposite sides. A natural photo does not do that at any s. A side whose
// band is flat (sky, wall) shows no jump and is left alone — a flat band
// cannot show a doubled object anyway.

const ZOOM_S_MIN = 1.01;
const ZOOM_S_MAX = 1.45;
/** Fine texture (rock, foliage) resamples badly at display size, so a band's
 *  copy error is judged mostly by the jump to the real photo (contrast). */
const ZOOM_MIN_CONTRAST = 2.5;
const ZOOM_MAX_ERR_VS_TEXTURE = 0.6;

interface SideProfile { view: View; side: Side; minDepth: number; maxDepth: number; uStep: number; tex: number[] }

function sideProfile(img: LumaImage, side: Side): SideProfile | null {
  const view = makeView(img, side);
  const maxDepth = Math.floor(view.H * EDGE_BAND_MAX_FRAC);
  const minDepth = Math.max(2, Math.ceil(view.H * 0.004));
  if (maxDepth <= minDepth || view.W < 16) return null;
  const uStep = Math.max(1, Math.floor(view.W / 240));
  const last = Math.min(view.H - 1, maxDepth + 12);
  const tex: number[] = [];
  for (let v = 0; v <= last; v++) {
    let m = 0, n = 0;
    for (let u = 0; u < view.W; u += uStep) { m += view.at(u, v); n++; }
    const mean = m / n;
    let t = 0;
    for (let u = 0; u < view.W; u += uStep) t += Math.abs(view.at(u, v) - mean);
    tex.push(t / n);
  }
  return { view, side, minDepth, maxDepth, uStep, tex };
}

interface ZoomSeam { k: number; before: number; after: number; texture: number; contrast: number }

/** Where, on this side, the zoomed copy at scale s stops: the k with the
 *  sharpest "copy before / no copy after" contrast. */
function zoomSeam(p: SideProfile, s: number): ZoomSeam | null {
  const { view, minDepth, maxDepth, uStep, tex } = p;
  const cu = (view.W - 1) / 2, cv = (view.H - 1) / 2;
  // The outermost band row must land inside the picture: k ≤ cv(1 − 1/s),
  // with 3 rows of slack for the coarse s grid and the blended seam row.
  const kCap = Math.min(maxDepth, Math.floor(cv - cv / s) + 3);
  if (kCap < minDepth) return null;
  const win = Math.max(3, Math.round(0.006 * view.H));
  const last = Math.min(tex.length - 1, kCap + win + 1);
  const e: number[] = [];
  for (let v = 0; v <= last; v++) {
    let acc = 0, n = 0;
    for (let u = 0; u < view.W; u += uStep) {
      acc += Math.abs(view.at(u, v) - sample(view, cu + (u - cu) / s, cv + (v - cv) / s));
      n++;
    }
    e.push(acc / n);
  }
  let best: ZoomSeam | null = null;
  let sumE = 0, sumT = 0;
  for (let k = 2; k <= kCap; k++) {
    // Running sums over the band rows [1, k−1): row 0 carries the resampler's
    // edge artefacts, and the seam row k−1 is blended.
    if (k >= 3) { sumE += e[k - 2]; sumT += tex[k - 2]; }
    if (k < minDepth) continue;
    const nb = k - 2;
    if (nb < 1) continue;
    const before = sumE / nb;
    const texture = sumT / nb;
    // "After" = the few rows just past the seam. A long window would reach the
    // real photo from ANY shallower k and make every k inside the band look
    // like a seam.
    const a = e.slice(k + 1, Math.min(e.length, k + 1 + win));
    if (a.length < 3) continue;
    const after = median(a);
    if (texture < BAND_MIN_TEXTURE || before > ZOOM_MAX_ERR_VS_TEXTURE * texture) continue;
    const contrast = after / Math.max(before, 0.75);
    if (contrast < ZOOM_MIN_CONTRAST) continue;
    if (!best || contrast > best.contrast) best = { k, before, after, texture, contrast };
  }
  return best;
}

/** Score of scale s: the best opposite pair whose seams agree in depth. */
function zoomPairScore(profiles: Partial<Record<Side, SideProfile>>, s: number): number {
  let score = 0;
  for (const [a, b] of [['top', 'bottom'], ['left', 'right']] as Array<[Side, Side]>) {
    const pa = profiles[a], pb = profiles[b];
    if (!pa || !pb) continue;
    const za = zoomSeam(pa, s);
    if (!za) continue;
    const zb = zoomSeam(pb, s);
    if (!zb) continue;
    const tol = Math.max(2, Math.round(0.004 * pa.view.H));
    if (Math.abs(za.k - zb.k) > tol) continue;
    score = Math.max(score, Math.min(za.contrast, zb.contrast));
  }
  return score;
}

/** The zoom factor of a centred zoomed background, or null if there is none. */
function findZoom(profiles: Partial<Record<Side, SideProfile>>): number | null {
  let bestS = 0, bestScore = 0;
  for (let s = ZOOM_S_MIN; s <= ZOOM_S_MAX + 1e-9; s += 0.003) {
    const sc = zoomPairScore(profiles, s);
    if (sc > bestScore) { bestScore = sc; bestS = s; }
  }
  if (bestScore <= 0) return null;
  for (let s = bestS - 0.003; s <= bestS + 0.003 + 1e-9; s += 0.0005) {
    if (s < ZOOM_S_MIN) continue;
    const sc = zoomPairScore(profiles, s);
    if (sc > bestScore) { bestScore = sc; bestS = s; }
  }
  return bestS;
}

export interface DetectOptions {
  /** Also look for a centred ZOOMED background (what our own importer used to
   *  paint into the letterbox). Only the repair of already-imported pages
   *  needs it: a client's PDF page never contains our composite, and on
   *  UI-like pages (white panels, thin rules) a zoom can fit by accident — one
   *  phone screenshot out of 54 natural photos did. Off by default. */
  zoom?: boolean;
}

/** Detect a fake band on each of the four sides. Nothing found → all zeros. */
export function detectEdgeBands(img: LumaImage, opts: DetectOptions = {}): EdgeBands {
  const res: EdgeBands = { top: 0, right: 0, bottom: 0, left: 0, findings: [] };
  const sides: Side[] = ['top', 'right', 'bottom', 'left'];
  const profiles: Partial<Record<Side, SideProfile>> = {};
  for (const side of sides) {
    const p = sideProfile(img, side);
    if (p) profiles[side] = p;
  }
  // 1. A centred zoomed background (our own importer's letterbox fill).
  const s = opts.zoom ? findZoom(profiles) : null;
  if (s !== null) {
    for (const side of sides) {
      const p = profiles[side];
      const z = p && zoomSeam(p, s);
      if (!p || !z) continue;
      const depth = Math.min(z.k + 1, p.maxDepth); // the blended seam row goes too
      res[side] = depth / p.view.H;
      res.findings.push({
        side, depth, frac: depth / p.view.H, model: 'zoom', scale: s,
        seamRatio: z.contrast, error: z.before, texture: z.texture, shiftedError: z.after,
      });
    }
    // A centred zoom has EQUAL bands on opposite sides. A side whose band is
    // flat (night sky, white wall) cannot show its seam, but it is not
    // evidence against one either: take the opposite side's depth. A textured
    // side that showed no seam stays uncut — that IS evidence against.
    const opposite: Record<Side, Side> = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };
    for (const side of sides) {
      const p = profiles[side];
      const o = res.findings.find((f) => f.side === opposite[side] && f.model === 'zoom');
      if (!p || res[side] > 0 || !o) continue;
      const depth = Math.min(o.depth, p.maxDepth);
      const band = p.tex.slice(1, Math.max(2, depth - 1));
      const texture = band.reduce((x, y) => x + y, 0) / band.length;
      if (texture >= BAND_MIN_TEXTURE) continue;
      res[side] = depth / p.view.H;
      res.findings.push({ ...o, side, depth, frac: depth / p.view.H, texture, seamRatio: 0 });
    }
  }
  // 2. A bleed made of the photo's own edge rows (duplicate / mirror / smear),
  //    on the sides the zoom did not explain.
  for (const side of sides) {
    if (res[side] > 0) continue;
    const f = detectSide(img, side);
    if (f) { res[side] = f.frac; res.findings.push(f); }
  }
  return res;
}

/** The pixel rectangle to keep in a W×H image (any resolution) once `bands`
 *  (fractions) are cut off. Rounds INWARD so no seam pixel survives. */
export function keepRect(width: number, height: number, bands: Pick<EdgeBands, 'top' | 'right' | 'bottom' | 'left'>): { x: number; y: number; width: number; height: number } {
  const x0 = Math.ceil(bands.left * width);
  const x1 = Math.floor((1 - bands.right) * width);
  const y0 = Math.ceil(bands.top * height);
  const y1 = Math.floor((1 - bands.bottom) * height);
  return { x: x0, y: y0, width: Math.max(1, x1 - x0), height: Math.max(1, y1 - y0) };
}

export function hasBands(b: Pick<EdgeBands, 'top' | 'right' | 'bottom' | 'left'>): boolean {
  return b.top > 0 || b.right > 0 || b.bottom > 0 || b.left > 0;
}

/** Run detection, crop (in fractions), and run it again on what is left — a
 *  client bleed can sit inside our own band. At most `passes` rounds. Returns
 *  the total fractions of the ORIGINAL image to cut. */
export function detectEdgeBandsNested(img: LumaImage, opts: DetectOptions = {}, passes = 2): EdgeBands {
  const total: EdgeBands = { top: 0, right: 0, bottom: 0, left: 0, findings: [] };
  let cur = img;
  // Rectangle of `cur` inside `img`, in pixels of `img`.
  let rx = 0, ry = 0, rw = img.width, rh = img.height;
  for (let pass = 0; pass < passes; pass++) {
    const b = detectEdgeBands(cur, opts);
    if (!hasBands(b)) break;
    total.findings.push(...b.findings);
    const r = keepRect(cur.width, cur.height, b);
    rx += r.x; ry += r.y; rw = r.width; rh = r.height;
    cur = cropLuma(img, rx, ry, rw, rh);
  }
  total.left = rx / img.width;
  total.top = ry / img.height;
  total.right = (img.width - rx - rw) / img.width;
  total.bottom = (img.height - ry - rh) / img.height;
  return total;
}

export function cropLuma(img: LumaImage, x: number, y: number, w: number, h: number): LumaImage {
  const out = new Float32Array(w * h);
  for (let r = 0; r < h; r++) {
    const src = (y + r) * img.width + x;
    for (let c = 0; c < w; c++) out[r * w + c] = img.data[src + c];
  }
  return { width: w, height: h, data: out };
}
